import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Server, Search, Lock, Users } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { isTeamRow } from '../../utils/ftpConnectionRows';
import { useSshConnections, connectionSubtitle } from './useSshConnections';
import useConnectionProjectGroups from '../../scope/useConnectionProjectGroups';
import { KIND_SSH } from '../../scope/connectionProjectGroups';
import ConnectionGroups from '../projectScope/ConnectionGroups';
import './TerminalExt.css';

/**
 * Modal picker over every SSH connection the user can reach — their own vault's
 * `sftp` items and every team vault they hold a grant in.
 *
 * TEAM ROWS ARE SAFE TO OFFER HERE, and were not always. The credential itself
 * never travels through this modal: the row's id does, and `ssh_term_open`
 * resolves the secret INSIDE Rust (`ssh_term.rs` — "there is no command that
 * returns SSH credentials"). A team row's id is `team:<teamId>:<itemId>`, which
 * Rust routes to the team vault's audited, per-folder-authorized HTTP endpoint;
 * a personal id resolves from the local table exactly as before. Neither shape
 * can reach the other's lookup.
 */
export default function SshConnectionModal({ isOpen, onClose, onPick }) {
  const { t } = useTranslation();
  const { connections: sshConnections, vaultLocked, unlocking, unlock } = useSshConnections(isOpen);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);
  const q = query.trim().toLowerCase();
  const visible = q
    ? sshConnections.filter((c) =>
        `${c.name || ''} ${c.teamName || ''} ${c.index?.username || ''}@${c.index?.host || ''}`
          .toLowerCase()
          .includes(q)
      )
    : sshConnections;
  // Project scope (card 5.4): the project's hosts on top, the rest below.
  const groups = useConnectionProjectGroups(visible, { kind: KIND_SSH, listId: 'terminal' });

  if (!isOpen) return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('terminalExt.pickConnectionTitle', 'Open SSH connection')}
      subtitle={t('terminalExt.pickConnectionSubtitle', 'Saved connections from your vault')}
    >
      {vaultLocked ? (
        <div className="term-ext-modal-locked">
          <Lock size={14} />
          <span>{t('ftp.vaultLockedSidebar', 'Vault is locked')}</span>
          <button
            type="button"
            className="settings-action-btn"
            disabled={unlocking}
            onClick={unlock}
          >
            {unlocking ? t('ftp.unlocking', 'Unlocking…') : t('ftp.unlock', 'Unlock')}
          </button>
        </div>
      ) : (
        <>
          {/* Same field base as other modal inputs (modal-invite-input is the
              with-left-icon variant of modal-input, e.g. invite fields). */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <Search
              size={14}
              className="text-gray-400"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }}
              aria-hidden
            />
            <input
              autoFocus
              type="text"
              className="modal-invite-input"
              style={{ width: '100%' }}
              placeholder={t('terminalExt.filterConnections', 'Filter connections')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="term-ext-modal-list panel-scroll">
            {visible.length === 0 ? (
              <div className="term-ext-modal-empty">
                {q
                  ? t('terminalExt.noMatches', 'No connections match your filter.')
                  : t('terminalExt.noConnections', 'No SSH connections in the vault yet.')}
              </div>
            ) : (
              <ConnectionGroups groups={groups} renderRow={(conn) => (
                <div key={conn.id} className="term-ext-row" onClick={() => onPick(conn)}>
                  <Server size={13} />
                  <span className="term-ext-row-name">
                    {conn.name}
                    {/* Which vault this host came from. A user about to open a
                        shell on production needs to know whether the
                        credential is theirs alone or the team's — the two have
                        different privacy properties and different audit
                        trails (a team credential read is logged server-side). */}
                    {isTeamRow(conn) && (
                      <span className="term-ext-row-team" title={conn.teamName}>
                        <Users size={10} aria-hidden />
                        {conn.teamName}
                      </span>
                    )}
                    <div className="term-ext-row-sub">{connectionSubtitle(conn)}</div>
                  </span>
                </div>
              )} />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
